import { useEffect, useRef } from 'react';

import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { usePostHog } from '@/hooks/use-posthog';

/**
 * Sends source_campaign:budget_edit_start once per opened campaign, and only
 * when its form is editable: a read-only or completed campaign renders as
 * "Campaign details" and no edit can start there.
 */
export function useBudgetEditStart() {
  const posthog = usePostHog();
  const {
    sourcingPurchaseId,
    campaignOpening,
    campaign,
    selectedPublisher,
    isReach,
    isCompleted,
  } = useEditCampaignContext();
  const sentFor = useRef<string>();

  // Reach derives isCompleted from the selected publisher, so it is unknown
  // until one is selected.
  const isLoaded = !!campaign && (!isReach || !!selectedPublisher);
  const isEditable = isLoaded && !campaign.is_readonly && !isCompleted;

  useEffect(() => {
    if (!campaign || !isEditable || sentFor.current === sourcingPurchaseId) {
      return;
    }
    sentFor.current = sourcingPurchaseId;

    // The campaigns row menu is the only way into this editor, so
    // campaign_row is the only entry point today.
    posthog.capture('source_campaign:budget_edit_start', {
      campaign_id: sourcingPurchaseId,
      opening_name: campaignOpening?.openingName,
      opening_id: campaignOpening?.openingId,
      channel: campaign.sourcing_channel.slug,
      budget_current: campaign.budget,
      entry_point: 'campaign_row',
    });
  }, [campaign, isEditable, sourcingPurchaseId, campaignOpening, posthog]);
}
